import { Button, Link, Stack, useToast } from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { RouterOutputs } from '@duke-hq/svc-api/admin';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Download, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { testIds } from './testIds';

type PlacementAgreement = Pick<
  RouterOutputs['workPlacements']['placements']['journeyByEnrolment']['placements'][number],
  'agreementFileDrns' | 'contractDocumentUrl'
>;

export function hasPlacementAgreement(placement: PlacementAgreement): boolean {
  return placement.contractDocumentUrl != null || placement.agreementFileDrns.length > 0;
}

export function PlacementAgreementLinks({
  placement,
}: {
  placement: PlacementAgreement;
}): React.ReactElement {
  const toast = useToast();
  const [loadingDrns, setLoadingDrns] = useState<Set<string>>(new Set());
  const getDownloadUrl = trpcAdmin.files.getDownloadUrl.useMutation();

  const handleDownload = async (fileDrn: string) => {
    setLoadingDrns((prev) => new Set(prev).add(fileDrn));
    try {
      const { url } = await getDownloadUrl.mutateAsync({ fileDrn });
      window.open(url, '_blank');
    } catch (error) {
      toast({
        status: 'error',
        title: t`Failed to download file`,
        description: error instanceof Error ? error.message : t`Unknown error`,
      });
    } finally {
      setLoadingDrns((prev) => {
        const next = new Set(prev);
        next.delete(fileDrn);
        return next;
      });
    }
  };

  return (
    <Stack gap={1} align="start">
      {placement.contractDocumentUrl != null ? (
        <Link
          href={placement.contractDocumentUrl}
          isExternal
          display="inline-flex"
          alignItems="center"
          gap={1}
          color="fg.accent.emphasis"
          fontWeight="medium"
          data-testid={testIds.agreementContractLink}
        >
          <ExternalLink size={iconSize.md} />
          <Trans>Placement agreement</Trans>
        </Link>
      ) : null}
      {placement.agreementFileDrns.map((drn, index) => (
        <Button
          key={drn}
          data-testid={testIds.agreementDownloadButton}
          variant="link"
          size="sm"
          colorScheme="blue"
          leftIcon={<Download size={iconSize.md} />}
          isLoading={loadingDrns.has(drn)}
          onClick={() => handleDownload(drn)}
        >
          {placement.agreementFileDrns.length > 1 ? (
            <Trans>Agreement {index + 1}</Trans>
          ) : (
            <Trans>Agreement</Trans>
          )}
        </Button>
      ))}
    </Stack>
  );
}
